import {
  useDeleteSealdAccess,
  useUpdateFileMutation,
} from "apollo/hooks/mutations";
import { useEnvContext } from "context/EnvContext";
import {
  FILE_SHARE_MODE_SELECTED,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import {
  addUsersToSession,
  computeSealdDisplayId,
  removeUsersFromSession,
} from "core/seald";
import {
  FileForm,
  FileParams,
  Filev2,
  QueryProgress,
  RadioOption,
} from "core/types";
import {
  GeneralListAction,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { FileShareResultWithInitialValues } from "dsl/organisms/Modals/FileSharingModal";
import { Dispatch, SetStateAction } from "react";
import { useTranslations } from "translations";

function getUniqueIds(arr1: RadioOption[], arr2: RadioOption[]) {
  return arr1.reduce<number[]>((acc, curr) => {
    if (!arr2.some(({ id }) => curr.id === id)) {
      return [...acc, Number(curr.id)];
    }
    return acc;
  }, []);
}

export function useShareFile<Data>({
  action,
  params,
  setAction,
  setProgress,
}: {
  action: GeneralListAction<FileForm | Filev2> | undefined;
  params: FileParams;
  setAction: SetAction<Data>;
  setProgress: Dispatch<SetStateAction<QueryProgress>>;
}) {
  const envContext = useEnvContext();
  const toast = useToast();
  const translations = useTranslations();

  const [updateFile] = useUpdateFileMutation({
    id: action?.data.id ?? -1,
    params,
  });

  const [deleteSealdAccess] = useDeleteSealdAccess();

  async function handleShareFile({
    initialValues,
    selected_receivers,
    share_mode,
  }: FileShareResultWithInitialValues) {
    try {
      setProgress(QUERY_PROGRESS_PENDING);

      const { data } = await updateFile({ share_mode });
      if (!data?.file) {
        throw Error("[handleShareFile] Couldn't update file");
      }

      const sessionId = data.file?.seald_encryption_context?.seald_id;

      const shouldUpdateSelectedCareproviders =
        share_mode === FILE_SHARE_MODE_SELECTED;

      const sealdPromises: Promise<void>[] = [];

      if (shouldUpdateSelectedCareproviders) {
        if (
          !initialValues.selected_receivers ||
          !selected_receivers ||
          !sessionId
        ) {
          throw new Error(
            "initialValues.selected_receivers, selected_receivers and sessionId must be defined",
          );
        }
        const removedCareproviderIds = getUniqueIds(
          initialValues.selected_receivers,
          selected_receivers,
        );
        const addedCareproviderIds = getUniqueIds(
          selected_receivers,
          initialValues.selected_receivers,
        );

        addedCareproviderIds.forEach((id) => {
          sealdPromises.push(
            addUsersToSession({
              entityUsersDisplayIds: [
                computeSealdDisplayId({
                  envContext,
                  id,
                  type: "careprovider",
                }),
              ],
              sessionId,
            }),
          );
        });

        removedCareproviderIds.forEach((id) => {
          sealdPromises.push(
            removeUsersFromSession({
              deleteSealdAccess,
              entityUsersDisplayIds: [
                computeSealdDisplayId({
                  envContext,
                  id,
                  type: "careprovider",
                }),
              ],
              sessionId,
            }),
          );
        });
      }

      await Promise.all(sealdPromises);
      setProgress(QUERY_PROGRESS_SUCCEED);
      toast({
        color: "success",
        message: translations.fileSection.fileShareComponent.toastSuccess,
      });
      setAction(undefined);
    } catch (err) {
      setProgress(QUERY_PROGRESS_FAILED);
      toast({ color: "danger", message: translations.fileSection.toastError });
      console.log(err);
    }
  }

  return { handleShareFile };
}
